import { movies } from "@/mocks/movies";
import type { Movie } from "@/types/movie";

export const PAGE_SIZE = 20;

export function getAllMovies(): Movie[] {
  return movies as Movie[];
}

export function getMovieById(id: string): Movie | undefined {
  return getAllMovies().find((movie) => movie.id === id);
}

export function getFeaturedMovies(): Movie[] {
  return getAllMovies().filter((movie) => movie.featured);
}

export function getLatestMovies(limit?: number): Movie[] {
  const sorted = [...getAllMovies()].sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.rating - a.rating;
  });
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotMovies(limit?: number): Movie[] {
  const sorted = [...getAllMovies()].sort((a, b) => b.views - a.views);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getRelatedMovies(movie: Movie, limit = 5): Movie[] {
  return getAllMovies()
    .filter((item) => item.id !== movie.id)
    .map((item) => ({
      item,
      score: item.genres.filter((genre) => movie.genres.includes(genre)).length,
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

interface FilterOptions {
  genre?: string;
  region?: string;
  year?: string;
  sort?: string;
}

export function filterMovies({
  genre = "全部",
  region = "全部",
  year = "全部",
  sort = "latest",
}: FilterOptions): Movie[] {
  const filtered = getAllMovies().filter((movie) => {
    const genreMatch =
      !genre || genre === "全部" || genre === "all" || movie.genres.includes(genre);
    const regionMatch = region === "全部" || movie.region === region;
    let yearMatch = true;
    if (year === "2023及更早") {
      yearMatch = movie.year <= 2023;
    } else if (year !== "全部") {
      yearMatch = movie.year === Number(year);
    }
    return genreMatch && regionMatch && yearMatch;
  });

  if (sort === "hot") {
    return filtered.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return filtered.sort((a, b) => b.rating - a.rating);
  }
  return filtered.sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return b.rating - a.rating;
  });
}

export function searchMovies(keyword: string): Movie[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return getAllMovies()
    .map((movie) => {
      let score = 0;
      if (movie.title.toLowerCase().includes(query)) score += 10;
      if (movie.enTitle.toLowerCase().includes(query)) score += 8;
      if (movie.director.toLowerCase().includes(query)) score += 4;
      if (movie.cast.some((name) => name.toLowerCase().includes(query))) score += 3;
      if (movie.genres.some((genre) => genre.toLowerCase().includes(query))) score += 3;
      if (movie.synopsis.toLowerCase().includes(query)) score += 1;
      if (movie.region.toLowerCase().includes(query)) score += 1;
      if (String(movie.year).includes(query)) score += 1;
      return { movie, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating)
    .map((entry) => entry.movie);
}

export function formatViews(views: number): string {
  if (views >= 100000000) {
    return `${(views / 100000000).toFixed(1)}亿`;
  }
  return `${(views / 10000).toFixed(1)}万`;
}

export function formatDuration(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  if (!hour) return `${minute}分钟`;
  return `${hour}小时${minute ? `${minute}分` : ""}`;
}